import { mtstatTrack } from '$/utils/mtstat';

export type AiToolUploadFunc = 'cut_out' | 'remove' | 'remove_aimark' | 'change_to_hd' | 'change_super_hd' | 'change_ai_hd';
export type AiToolUploadSource =
  | 'loading_page'
  | 'reload'
  | 'canvas'
  | 'add_bigimage'
  | 'load_background'
  | 'batch_continue_load';
export type AiToolRunGenerateType = 1 | 2 | '1' | '2';
export type AiToolTrackContentType = 'image' | 'video' | 'image,video';
export type AiToolCutoutType = 'cutout' | 'background_replace';

const AI_TOOL_VIP_MODAL_TRACK_CONFIG: Record<AiToolUploadFunc, {
  toolId: string;
  toolName: string;
}> = {
  cut_out: {
    toolId: 'ai_cutout',
    toolName: 'AI抠图',
  },
  remove: {
    toolId: 'ai_eraser',
    toolName: 'AI消除',
  },
  remove_aimark: {
    toolId: 'ai_watermark',
    toolName: 'AI去水印',
  },
  change_to_hd: {
    toolId: 'ai_enhance',
    toolName: 'AI变高清',
  },
  change_super_hd: {
    toolId: 'ai_enhance',
    toolName: 'AI变高清',
  },
  change_ai_hd: {
    toolId: 'ai_enhance',
    toolName: 'AI变高清',
  },
};

interface BuildAiToolVipModalTrackOptions {
  entrance?: 'insufficient_balance';
  apiName?: string;
}

interface TrackAiToolRunInput {
  func: AiToolUploadFunc;
  taskId?: string;
  taskIds?: Array<string | undefined>;
  prompt?: string;
  generateType: AiToolRunGenerateType;
  removeAutoType?: string | string[];
  manualType?: string;
  manualLineSize?: string | number;
  changeToHdType?: string;
  enhanceImage?: string;
  enhanceVideo?: string;
  autoFindType?: string;
  colorType?: string;
  backgroundUrl?: string;
  aiCutout?: AiToolCutoutType;
}

interface TrackAiToolRunSuccessInput extends TrackAiToolRunInput {
  resultUrl?: string;
  resultUrls?: Array<string | undefined>;
  contentType: AiToolTrackContentType;
}

interface TrackAiToolDownloadSuccessInput extends TrackAiToolRunInput {
  saveUrl?: string;
  saveUrls?: Array<string | undefined>;
  contentType: AiToolTrackContentType;
  downMode: 1 | 2 | '1' | '2';
  number: number | string;
}

interface TrackAiToolUploadInput {
  func: AiToolUploadFunc;
  source: AiToolUploadSource;
  file?: File | null;
  url?: string;
  contentType?: 'image' | 'video';
}

/**
 * 从文件或 URL 中提取上报约定的内容格式。
 */
export function getAiToolUploadContentFormat(input?: File | string | null) {
  if (!input) return '';
  const rawName = typeof input === 'string' ? input : input.name || input.type;
  const path = rawName.split('?')[0].split('#')[0];
  const lastSegment = path.split('/').pop() || '';
  const ext = lastSegment.includes('.') ? lastSegment.split('.').pop()?.trim().toLowerCase() || '' : '';
  if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'heic' || ext === 'mp4') {
    return ext;
  }
  if (typeof input !== 'string') {
    const mimeExt = input.type.split('/')[1]?.toLowerCase() || '';
    if (mimeExt === 'jpeg') return 'jpeg';
    if (mimeExt === 'png' || mimeExt === 'heic' || mimeExt === 'mp4') return mimeExt;
  }
  return ext;
}

/**
 * 根据文件 MIME 或 URL 后缀推断内容类型。
 */
export function getAiToolUploadContentType(input?: File | string | null) {
  if (!input) return '';
  if (typeof input !== 'string' && input.type.startsWith('video/')) return 'video';
  const format = getAiToolUploadContentFormat(input);
  return format === 'mp4' ? 'video' : 'image';
}

/**
 * 组装 AI 创作工具上传埋点参数；点击时无素材则省略格式字段，成功时用真实素材补齐。
 */
function buildAiToolUploadParams(input: TrackAiToolUploadInput) {
  const material = input.file || input.url || null;
  return compactTrackParams({
    func: input.func,
    source: input.source,
    content_type: input.contentType || getAiToolUploadContentType(material),
    content_format: getAiToolUploadContentFormat(material),
  });
}

/**
 * 组装 AI 单工具打开订阅弹窗的公共埋点参数。
 */
export function buildAiToolVipModalTrackParams(
  func: AiToolUploadFunc,
  options: BuildAiToolVipModalTrackOptions = {},
) {
  const config = AI_TOOL_VIP_MODAL_TRACK_CONFIG[func];
  return {
    position_type: 'external_tool_panel',
    tool_id: config.toolId,
    tool_name: config.toolName,
    func,
    ...(options.entrance ? { entrance: options.entrance } : {}),
    ...(options.apiName ? { api_name: options.apiName } : {}),
  };
}

/**
 * 清理埋点参数空值，避免把未命中的可选参数上报成空字符串。
 */
function compactTrackParams(params: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );
}

/**
 * 上报 AI 创作工具上传点击埋点。
 */
export function trackAiToolUploadClick(input: TrackAiToolUploadInput) {
  mtstatTrack('roboneo_aifunc_upload_click', buildAiToolUploadParams(input));
}

/**
 * 上报 AI 创作工具上传成功埋点。
 */
export function trackAiToolUploadSuccess(input: TrackAiToolUploadInput) {
  mtstatTrack('roboneo_aifunc_upload_yes', buildAiToolUploadParams(input));
}

/**
 * 上报 AI 单工具功能页进入埋点。
 */
export function trackAiToolLandingPageEnter(func: AiToolUploadFunc) {
  mtstatTrack('roboneo_landing_page_enter', { class: func });
}

interface TrackAiToolOpenWorkflowInput {
  func: AiToolUploadFunc;
  taskIds?: Array<string | undefined>;
  urlList?: Array<string | undefined>;
}

/**
 * 将顶部按钮埋点的数组参数按数据表要求转换为逗号分隔字符串。
 */
function joinTrackList(values?: Array<string | undefined>) {
  return (values || []).filter(Boolean).join(',');
}

/**
 * 将批量素材类型按埋点口径合并，混合下载固定上报 image,video。
 */
export function joinAiToolTrackContentTypes(values: Array<'image' | 'video' | undefined>): AiToolTrackContentType {
  const hasImage = values.includes('image');
  const hasVideo = values.includes('video');
  if (hasImage && hasVideo) return 'image,video';
  if (hasVideo) return 'video';
  return 'image';
}

/**
 * 将 countcost 的 tool_name 去重拼接成订阅弹窗埋点的 api_name。
 */
export function joinAiToolTrackApiNames(values: Array<string | undefined>) {
  return Array.from(new Set(values.filter(Boolean))).join(',');
}

/**
 * 将单工具运行埋点的数组型参数转换为数据平台约定的字符串。
 */
function normalizeRunTrackValue(value?: string | string[]) {
  if (Array.isArray(value)) return value.filter(Boolean).join(',');
  return value;
}

/**
 * 构建 AI 单工具执行类埋点参数，点击和成功事件共用同一套字段口径。
 */
function buildAiToolRunParams(input: TrackAiToolRunInput) {
  return compactTrackParams({
    func: input.func,
    task_id: input.taskId || joinTrackList(input.taskIds),
    prompt: input.prompt,
    generate_type: String(input.generateType),
    remove_auto_type: normalizeRunTrackValue(input.removeAutoType),
    manual_type: input.manualType,
    manual_line_size: input.manualLineSize === undefined ? undefined : String(input.manualLineSize),
    change_tohd_type: input.changeToHdType,
    enhance_image: input.enhanceImage,
    enhance_video: input.enhanceVideo,
    auto_find_type: input.autoFindType,
    color_type: input.colorType,
    background_url: input.backgroundUrl,
    ai_cutout: input.aiCutout,
  });
}

/**
 * 将画布手动消除模式转换为数据平台约定的 manual_type。
 */
export function getAiToolManualRunType(type?: string) {
  if (type === 'smear') return 'daub';
  if (type === 'rect') return 'select';
  return undefined;
}

/**
 * 上报 AI 单工具点击生成后的任务创建埋点，按获取到 task_id 的时机触发。
 */
export function trackAiToolRunClick(input: TrackAiToolRunInput) {
  mtstatTrack('roboneo_aifunc_run_click', buildAiToolRunParams(input));
}

/**
 * 上报 AI 单工具任务执行成功埋点，按最终结果 URL 获取成功的时机触发。
 */
export function trackAiToolRunSuccess(input: TrackAiToolRunSuccessInput) {
  mtstatTrack('roboneo_aifunc_run_yes', compactTrackParams({
    ...buildAiToolRunParams(input),
    result_url: input.resultUrl || joinTrackList(input.resultUrls),
    content_type: input.contentType,
  }));
}

/**
 * 上报 AI 单工具下载成功埋点，按下载动作完成后的时机触发。
 */
export function trackAiToolDownloadSuccess(input: TrackAiToolDownloadSuccessInput) {
  mtstatTrack('roboneo_aifunc_download_success', compactTrackParams({
    ...buildAiToolRunParams(input),
    save_url: input.saveUrl || joinTrackList(input.saveUrls),
    content_type: input.contentType,
    down_mode: String(input.downMode),
    number: String(input.number),
  }));
}

/**
 * 上报点击 AI 单工具顶部“打开工作流”按钮埋点。
 */
export function trackAiToolOpenWorkflow(input: TrackAiToolOpenWorkflowInput) {
  mtstatTrack('roboneo_aifunc_to_open_workflow', {
    func: input.func,
    task_id: joinTrackList(input.taskIds),
    url_list: joinTrackList(input.urlList),
  });
}
